// nativePush.js — native iOS (APNs) push for the Capacitor wrapper.
//
// Web Push (pushUtils.js) does NOT work inside the iOS WKWebView — Apple
// doesn't expose the Push API there. So when MFML runs as the native app we
// register for APNs via the Capacitor Push Notifications plugin, store the
// device token in `apns_tokens`, and the send-push edge function delivers to
// it over APNs HTTP/2. On the web this module is a no-op and pushUtils.js
// handles everything as before.
//
// The message TRIGGERS are unchanged — every sendPushNotification() call
// across DMView / ChannelView / SixGuysRP / DialMyFate already fires for both
// human and AI-character messages. This module only adds a delivery channel.

import { supabase } from "./supabaseClient.js";
import { scheduleBadgeSync } from "./appBadge.js";

let Capacitor = null;
let PushNotifications = null;
let wired = false;
// True once this device has registered for APNs in this session. The Settings
// panel used to gate the whole notification-preferences block on the WEB push
// subscription, which cannot exist inside the iOS wrapper — so the native app
// never showed the block at all (2026-10-19 audit: Nicole and Brooke, APNs
// only, could not reach Every message / Bundled, the category switches or
// Snooze; the writers' 'instant' rows had been set by SQL).
let nativeRegistered = false;
export function isNativePushActive() { return nativeRegistered; }

// Lazy-load the Capacitor plugins so the web bundle never pulls native code.
async function loadPlugins() {
  if (Capacitor && PushNotifications) return true;
  try {
    const core = await import("@capacitor/core");
    Capacitor = core.Capacitor;
    if (!Capacitor?.isNativePlatform?.()) return false;
    const pn = await import("@capacitor/push-notifications");
    PushNotifications = pn.PushNotifications;
    return true;
  } catch (err) {
    console.warn("[NativePush] Plugin load failed (web build?):", err?.message || err);
    return false;
  }
}


async function storeToken(userId, token) {
  if (!userId || !token) return;
  try {
    // Upsert on the unique token: same device re-registering just refreshes
    // ownership + updated_at; a token that moved to a new user reattaches.
    const { error } = await supabase
      .from("apns_tokens")
      .upsert(
        { user_id: userId, token, environment: "production", updated_at: new Date().toISOString() },
        { onConflict: "token" }
      );
    if (error) console.warn("[NativePush] Token upsert failed:", error.message);
    else console.log("[NativePush] APNs token registered for user", userId);
  } catch (err) {
    console.warn("[NativePush] storeToken error:", err?.message || err);
  }
}

/**
 * Initialize native push for the signed-in user. Safe to call on web (no-op)
 * and safe to call repeatedly (listeners are wired once). Requests the OS
 * permission prompt on first run, then registers for APNs.
 */
export async function initNativePush(userId) {
  const ok = await loadPlugins();
  if (!ok || !PushNotifications) return false;

  try {
    // Ask once; if already granted/denied this returns the existing state.
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      console.log("[NativePush] Permission not granted:", perm.receive);
      return false;
    }

    if (!wired) {
      wired = true;

      // APNs returned a device token — persist it.
      PushNotifications.addListener("registration", (tokenData) => {
        const token = tokenData?.value;
        if (token) storeToken(userId, token);
      });

      PushNotifications.addListener("registrationError", (err) => {
        console.warn("[NativePush] Registration error:", err?.error || err);
      });

      // Foreground delivery: the OS does NOT show a banner while the app is
      // open, so MFML's in-app notification UI already covers that case. We
      // just log here to avoid double-notifying.
      PushNotifications.addListener("pushNotificationReceived", (notif) => {
        scheduleBadgeSync();
        console.log("[NativePush] Foreground push:", notif?.title);
      });

      // User tapped the notification — deep-link via the payload `url`.
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        scheduleBadgeSync();
        try {
          const url = action?.notification?.data?.url;
          if (url && typeof url === "string") {
            // Same-origin route inside the wrapper.
            window.location.href = url.startsWith("http") ? url : url;
          }
        } catch (err) {
          console.warn("[NativePush] Tap handling failed:", err?.message || err);
        }
      });
    }

    await PushNotifications.register();
    nativeRegistered = true;
    return true;
  } catch (err) {
    console.warn("[NativePush] init failed (non-fatal):", err?.message || err);
    return false;
  }
}

